// src/pages/SelectionPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useAppContext } from '../context/AppContext';
import {
  getMe, getFacilities, getDepartments,
  getShifts, getMinDate
} from '../services/api';
import LoadingPage from './LoadingPage';

// Date helpers
const today   = () => new Date().toISOString().split('T')[0];
const maxDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; };

const formatDate = (d) => {
  if (!d) return null;
  const [year, month, day] = d.split('-');
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

export default function SelectionPage() {
  const { associateId: authAssociateId } = useAuth();
  const { user, setUser, setSelection }  = useAppContext();
  const navigate                         = useNavigate();

  const [loading,      setLoading]      = useState(!user);
  const [facilities,   setFacilities]   = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [shifts,       setShifts]       = useState([]);
  const [facilityId,   setFacilityId]   = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [shiftId,      setShiftId]      = useState('');
  const [shiftDate,    setShiftDate]    = useState(today());
  const [errors,       setErrors]       = useState({});
  const [apiError,     setApiError]     = useState('');
  const [minDate,      setMinDate]      = useState('');
  const [payPeriodStart, setPayPeriodStart] = useState('');

  // ── Bootstrap: fetch user access ──────────────────────────────────────────
  useEffect(() => {
    if (!authAssociateId) return;
    if (user) { bootstrapFacilities(user.homeFacilityId, user.isCorporate); return; }

    // Prime the API auth header before any calls
    getMe()
      .then(res => {
        setUser({
          ...res.data,
          displayName: res.data.displayName || res.data.associateId
        });
        bootstrapFacilities(res.data.homeFacilityId, res.data.isCorporate);
      })
      .catch(err => {
        if (err.response?.status === 403) navigate('/unauthorized');
        else setApiError('Failed to load user information. Please refresh.');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAssociateId]);

  const bootstrapFacilities = useCallback((homeFacilityId, isCorporate) => {
    getFacilities(homeFacilityId, isCorporate)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setFacilities(data);
        if (data.length === 1) setFacilityId(data[0].guidFacilityID);
      })
      .catch(() => setApiError('Failed to load facilities.'))
      .finally(() => setLoading(false));
  }, []);

  // ── Cascade: facility → departments ──────────────────────────────────────
  useEffect(() => {
    if (!facilityId) { setDepartments([]); setDepartmentId(''); return; }
    getDepartments(facilityId)
      .then(res => { setDepartments(res.data); setDepartmentId(''); setShifts([]); setShiftId(''); })
      .catch(() => setApiError('Failed to load departments.'));
  }, [facilityId]);

  // ── Cascade: department → shifts ─────────────────────────────────────────
  useEffect(() => {
    if (!departmentId) { setShifts([]); setShiftId(''); return; }
    getShifts(departmentId)
      .then(res => { setShifts(res.data); setShiftId(''); })
      .catch(() => setApiError('Failed to load shifts.'));
  }, [departmentId]);

  // ── Min date from database ────────────────────────────────────────────────
  useEffect(() => {
    getMinDate()
      .then(res => {
        setMinDate(res.data.minDate);
        setPayPeriodStart(res.data.payPeriodStart);
      })
      .catch(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        setMinDate(d.toISOString().split('T')[0]);
        console.warn('Could not load min date from database, using default.');
      });
  }, []);

  // ── Validation & GO ───────────────────────────────────────────────────────
  const handleGo = () => {
    const errs = {};
    if (!facilityId)   errs.facility   = 'Please select a facility.';
    if (!departmentId) errs.department = 'Please select a department.';
    if (!shiftId)      errs.shift      = 'Please select a shift.';
    if (!shiftDate)    errs.shiftDate  = 'Please select a shift date.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const facility   = facilities.find(f => f.guidFacilityID   === facilityId);
    const department = departments.find(d => d.guidDepartmentID === departmentId);
    const shift      = shifts.find(s => s.guidShiftID           === shiftId);

    setSelection({ facility, department, shift, shiftDate, facilityId, departmentId, shiftId, minDate, payPeriodStart });
    navigate('/leadpay');
  };

  if (loading) return <LoadingPage message="Verifying access…" />;

  return (
    <div className="page-center">
      <div style={{ width: '100%', maxWidth: 520 }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: 'var(--navy)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: '1.8rem', margin: '0 auto 1rem', color: 'var(--gold)'
          }}>💰</div>
          <h1 style={{ marginBottom: '.4rem' }}>Lead Pay Portal</h1>
          <p style={{ color: 'var(--gray-600)', fontSize: '.9rem' }}>
            Select your criteria to get started
          </p>
        </div>

        <div className="card">
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

            {apiError && <div className="alert alert-error">{apiError}</div>}

            {/* Facility */}
            <div className="form-group">
              <label className="form-label">Facility</label>
              <select
                className={`form-control${errors.facility ? ' error' : ''}`}
                value={facilityId}
                onChange={e => setFacilityId(e.target.value)}
              >
                <option value="">— Select Facility —</option>
                {facilities.map(f => (
                  <option key={f.guidFacilityID} value={f.guidFacilityID}>{f.strName}</option>
                ))}
              </select>
              {errors.facility && <span className="form-error">{errors.facility}</span>}
            </div>

            {/* Department */}
            <div className="form-group">
              <label className="form-label">Department</label>
              <select
                className={`form-control${errors.department ? ' error' : ''}`}
                value={departmentId}
                onChange={e => setDepartmentId(e.target.value)}
                disabled={!facilityId}
              >
                <option value="">— Select Department —</option>
                {departments.map(d => (
                  <option key={d.guidDepartmentID} value={d.guidDepartmentID}>{d.strName}</option>
                ))}
              </select>
              {errors.department && <span className="form-error">{errors.department}</span>}
            </div>

            {/* Shift */}
            <div className="form-group">
              <label className="form-label">Shift</label>
              <select
                className={`form-control${errors.shift ? ' error' : ''}`}
                value={shiftId}
                onChange={e => setShiftId(e.target.value)}
                disabled={!departmentId}
              >
                <option value="">— Select Shift —</option>
                {shifts.map(s => (
                  <option key={s.guidShiftID} value={s.guidShiftID}>{s.strName}</option>
                ))}
              </select>
              {errors.shift && <span className="form-error">{errors.shift}</span>}
            </div>

            {/* Shift Date */}
            <div className="form-group">
              <label className="form-label">Shift Date</label>
              <input
                type="date"
                className={`form-control${errors.shiftDate ? ' error' : ''}`}
                value={shiftDate}
                min={minDate}
                max={maxDate()}
                onChange={e => setShiftDate(e.target.value)}
              />
              {errors.shiftDate && <span className="form-error">{errors.shiftDate}</span>}
              <span style={{ fontSize: '.75rem', color: 'var(--gray-400)' }}>
                Range: from start of the open pay period through tomorrow.
                {payPeriodStart && (
                  <> Dates before <strong>{formatDate(payPeriodStart)}</strong> are historical and will be read-only.</>
                )}
              </span>
            </div>

            <button
              className="btn btn-gold btn-lg"
              style={{ marginTop: '.5rem', width: '100%', justifyContent: 'center', fontSize: '1rem' }}
              onClick={handleGo}
            >
              GO →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
